import { JSX }		from "preact";
import { useId }	from "preact/hooks";

import { Arrays }	from "ts-base/arrays";
import { Dom }		from "ts-base/web/dom";

import { Permission }			from "@v3/data/permission";

import { Modal }				from "@v3/geko/Modal";
import { ProvidePermissions }	from "@v3/geko/permission/usePermissions";

import { availablePermissions, permissions }	from "@geotoura/shared/authorization";

import { Logger }	from "@geotoura/shared/logger";
import * as i18n	from "@geotoura/shared/i18n";
import * as upModel	from "@geotoura/shared/upModel";

import { ProvideLanguageCode }	from "@geotoura/common/util/useLanguageCode";

import { UpData, UpDataProvider }	from "@geotoura/up/upData";
import * as util from "@geotoura/up/util";

import { ProvideMessages }	from "@geotoura/up/useMessages";
import { Messages }			from "@geotoura/up/locale";
import {
	Model,
	Route as ModelRoute,
	RouteStats,
}	from "@geotoura/up/model";
import * as routes	from "@geotoura/up/routes";
import * as actions	from "@geotoura/up/actions";

import { GMap }				from "@geotoura/up/gui/GMap";
import { Destination }		from "@geotoura/up/gui/Destination";
import { Help }				from "@geotoura/up/gui/Help";
import { Howto }			from "@geotoura/up/gui/Howto";
import { Search }			from "@geotoura/up/gui/Search";
import { Legend }			from "@geotoura/up/gui/Legend";
import { TerritoryList }	from "@geotoura/up/gui/TerritoryList";
import { RequestRoute }		from "@geotoura/up/gui/RequestRoute";
import { RequestForm }		from "@geotoura/up/gui/RequestForm";
import { RouteBar }			from "@geotoura/up/gui/RouteBar";
import { Route }			from "@geotoura/up/gui/Route";

const logger	= Logger.create("App");

export type AppProps	= Readonly<{
	upData:			UpData,
	languageCode:	i18n.LanguageCode,
	messages:		Messages,
	model:			Model,
}>;

export const App = ({ upData, languageCode, messages, model }:AppProps):JSX.Element => {
	const destinationUid	= useId();

	const stats:RouteStats = routeStats(model.route);

	// TODO route this is very similar to the one in Route.tsx - but _not_ the same. why?
	// TODO route move this into actions
	const removeAction = () => {
		if (model.route.index === model.route.items.length - 1) {
			actions.toggleAdded();
		}
		else {
			void actions.removeActiveDestination(upData, languageCode, model.route.items[model.route.index + 1].id);
		}
	};

	// TODO route move this into actions
	const loadSavedRoute = () =>
		actions.loadSavedRoute(upData, languageCode, model.savedRoute[0].id);

	// TODO route move this into actions
	const navigate = (dir:"previous"|"next") => {
		const newRouteIndex	= util.step(model.route.index, model.route.items.length, dir);
		if (newRouteIndex === null) {
			logger.error("tried to move beyond route, this should not have been possible");
			return;
		}
		void actions.selectDestination(upData, languageCode, model.route.items[newRouteIndex].id, newRouteIndex, false);
	};

	return (
		<ProvidePermissions value={permissionSet}>
		<UpDataProvider value={upData}>
		<ProvideLanguageCode value={languageCode}>
		<ProvideMessages value={messages}>
			<div class={Dom.classes(
				"app",
				model.screenSize.orientation,
				model.screenSize.mode,
			)}>
				{ model.viewRegions	&&
					<TerritoryList screenSize={model.screenSize}/>
				}
				{ model.modalHelp &&
					<Modal
						title={messages.howtoHelp.title}
						confirm={{
							title:			messages.howtoHelp.buttonTitle,
							action:			actions.toggleHelp,
							permission:		permissions.useWebsite,
							submitOnEnter:	true,
						}}
						cancel={{
							title:	messages.general.cancel,
							action:	actions.toggleHelp,
						}}
						close={actions.toggleHelp}
					>
						<Help/>
					</Modal>
				}
				{ model.modalHowto &&
					<Modal
						title={messages.howtoStart.title}
						confirm={{
							title:			messages.howtoStart.buttonTitle,
							action:			() => void actions.confirmStart(upData, languageCode, model.exampleRouteData.exampleRoute.value),
							permission:		permissions.useWebsite,
							submitOnEnter:	true,
						}}
						cancel={{
							title:	messages.general.cancel,
							action:	actions.toggleStart,
						}}
						close={actions.toggleStart}
					>
						<Howto
							modalHowtoRegionIds={model.modalHowtoRegionIds}
							exampleRouteData={model.exampleRouteData}
							modifyExampleRouteForm={actions.modifyExampleRouteForm}
						/>
					</Modal>
				}
				<div class={`app-map ${!model.drawerState.open ? "expanded" : ""}`}>
					<Search destinationUid={destinationUid}/>
					<GMap
						screenSize={model.screenSize}
						mapDefaults={model.mapDefaults}
						setMapViewEvent={model.setMapViewEvent}
						route={model.route}
					/>
					<Legend screenSize={model.screenSize}/>
				</div>
				<div
					onTransitionEnd={() => actions.drawerTransitionEnd() }
					class={Dom.classes(
						"app-content",
						model.drawerState.open			&& "expanded",
						model.drawerState.transition	&& "transitioning",
						model.destination !== null && model.drawerState.content === "destination" ? "app-content-destination" : "app-content-route",
						(model.destinationState.hotelGallery.open || model.destinationState.destinationGallery.open) && "lightbox"
					)}
				>
					{ model.screenSize.orientation === "landscape" &&
						<button
							class="app-content-toggler"
							title={model.drawerState.open ? messages.app.contentHide : messages.app.contentShow}
							type="button"
							onClick={actions.toggleDrawer}
						>
						</button>
					}
					<div class="app-content-inner">
					{ model.destination !== null && model.drawerState.content === "destination" ?
						<Destination
							destinationUid={destinationUid}
							screenSize={model.screenSize}
							destination={model.destination}
							state={model.destinationState}
							route={model.route}
							nearBysSorted={sortNearBys(model.destination.destinationsNearBy, model.route.items)}
							removeAction={removeAction}
						/>
						:
						<Route
							orientation={model.screenSize.orientation}
							route={model.route}
							stats={stats}
						/>
					}
					</div>
				</div>
				{ !(model.modalHowto && model.screenSize.orientation === "portrait") && !model.viewRegions &&
					<div class="app-route">
						<RouteBar
							orientation={model.screenSize.orientation}
							stats={stats}
							showStation={model.route.index > -1}
							drawerState={model.drawerState}
							navigate={navigate}
						/>
					</div>
				}
				{ model.modalRequest && model.requestResponse === null &&
					<Modal
						title={messages.request.title}
						confirm={{
							title:			model.screenSize.mode === "compact" ? messages.request.buttonRequestShort: messages.request.buttonRequest,
							action:			() => actions.sendRequest(languageCode),
							permission:		permissions.useWebsite,
							submitOnEnter:	false,
						}}
						cancel={{
							title:	messages.general.cancel,
							action:	actions.toggleRequest,
						}}
						close={actions.toggleRequest}
					>
						<div class="request">
							<div class="request-route">
								<RequestRoute
									route={model.route}
									stats={stats}
								/>
							</div>
							<div class="request-form">
								<RequestForm
									flightEnabled={model.flightEnabled}
									personalData={model.personalData}
									flightData={model.flightData}
									modifyPersonal={actions.modifyPersonalForm}
									modifyFlight={actions.modifyFlightForm}
								/>
							</div>
						</div>
					</Modal>
				}
				{ model.requestEnroute &&
					<div class="loading">
						<div class="loading-inner">
							<div class="loading-spinner fa fa-spin fa-refresh"></div>
						</div>
					</div>
				}
				{ model.requestResponse !== null && model.requestResponse.return === "ok" &&
					<Modal
						title={messages.requestSuccess.title}
						confirm={{
							title:			messages.general.closeDialog,
							action:			actions.closeRequest,
							permission:		permissions.useWebsite,
							submitOnEnter:	true,
						}}
						close={actions.closeRequest}
					>
						{ /* DO NOT CHANGE: "conversion-planner" is used for conversion tracking by Google Tag Manager */ }
						<p class="conversion-planner">{messages.requestSuccess.text1}</p>
						<p>{messages.requestSuccess.text2}</p>
						<p>{messages.requestSuccess.text3}</p>
						<p>{messages.requestSuccess.greetings}</p>
						<div class="signature">{messages.requestSuccess.signature}</div>
					</Modal>
				}
				{ model.requestResponse !== null && model.requestResponse.return === "error" &&
					<Modal
						title={messages.requestError.title}
						confirm={{
							title:			messages.general.closeDialog,
							action:			actions.closeRequest,
							permission:		permissions.useWebsite,
							submitOnEnter:	true,
						}}
						close={actions.closeRequest}
					>
						<p>
							{messages.requestError.text} <a href={messages.requestError.link.href}>{messages.requestError.link.text}</a>.
						</p>
					</Modal>
				}
				{ model.modalRouteDelete &&
					<Modal
						title={messages.routeDelete.title}
						confirm={{
							title:			messages.routeDelete.confirmTitle,
							action:			actions.clearRoute,
							permission:		permissions.useWebsite,
							submitOnEnter:	false,
						}}
						cancel={{
							title:	messages.general.cancel,
							action:	actions.toggleRouteDelete,
						}}
						close={actions.toggleRouteDelete}
					>
						<p>{messages.routeDelete.message}</p>
					</Modal>
				}
				{ model.modalOverwriteRoute && model.entryPoint.route !== null &&
					<Modal
						title={messages.overwriteRoute.title}
						confirm={{
							title:			messages.overwriteRoute.confirmTitle,
							action:			() => void actions.loadExampleRoute(upData, languageCode, model.entryPoint.route),
							permission:		permissions.useWebsite,
							submitOnEnter:	false,
						}}
						cancel={{
							title:	messages.overwriteRoute.cancelTitle,
							action:	() => void loadSavedRoute(),
						}}
						close={() => void loadSavedRoute()}
					>
						<p>{messages.overwriteRoute.message1} <strong>»{upData.route.getOrNull(model.entryPoint.route)?.name ?? ""}«</strong> {messages.overwriteRoute.message2} {model.savedRoute.length} {model.savedRoute.length === 1 ? messages.general.stop : messages.general.stops} {messages.overwriteRoute.message3}</p>
					</Modal>
				}
			</div>
		</ProvideMessages>
		</ProvideLanguageCode>
		</UpDataProvider>
		</ProvidePermissions>
	);
};

const routeStats	= (route:ModelRoute):RouteStats => {
	const currentRoute = routes.regularItems(route);

	return ({
		stations:		currentRoute.length,
		currentStation: route.added ? route.index + 1 : null,
		isFirst:		route.index === 0,
		isLast:			route.index === route.items.length - 1,
		nights:			util.sumBy(currentRoute, it => it.nights),
		activities:		util.sumBy(currentRoute, it => it.activities.length),
		wishes:			util.sumBy(currentRoute, it => it.wish === "" ? 0 : 1),
	});
};

const sortNearBys = (nearBys:ReadonlyArray<upModel.DestinationNearBy>, route:ReadonlyArray<upModel.RouteItem>) => {
	const idsInRoute = route.map(it => it.id);
	const compareNearBys = (a:upModel.DestinationNearBy, b:upModel.DestinationNearBy) => {
		const foundA = idsInRoute.includes(a.id);
		const foundB = idsInRoute.includes(b.id);
		return foundA === foundB ? 0 : foundA ? 1 : -1;
	};
	return Arrays.sortWith(nearBys)(compareNearBys);
};

//-----------------------------------------------------------------------------

// right now, we assume everyone is a (logged in...) customer
// and nobody is ever an Unauthenticated
const permissionSet:ReadonlySet<Permission>	=
	availablePermissions({
		roles:	[ "customer" ],
	});
